<template>
    <Teleport to="body">
        <div v-if="award" class="fso-overlay" role="dialog" aria-modal="true" aria-labelledby="sgaOverlayTitle">
            <div class="fso-backdrop" />

            <div
                class="fso-card"
                :class="{
                    'fso-card--has-image': theme.image,
                    'fso-card--gold-takeover': isTakeover,
                }"
                :style="cssVars"
            >
                <button class="fso-sticky-close" aria-label="Close" @click="close('close')">&times;</button>

                <div class="fso-orb fso-orb--1" aria-hidden="true" />
                <div class="fso-orb fso-orb--2" aria-hidden="true" />

                <!-- ── Decoration area ──────────────────────────────────
                     Takeover swaps the entire decoration region.
                     - With performer context  → performer hero
                     - Without performer       → Gold PSA video poster
                     - Other variants          → original star-field, etc.
                     ──────────────────────────────────────────────────── -->
                <div v-if="isTakeover && hasPerformer" class="fso-performer-hero">
                    <div
                        class="fso-performer-hero__bg"
                        :style="{ backgroundImage: performerData ? `url(${performerData.image})` : '' }"
                        aria-hidden="true"
                    />
                    <div class="fso-performer-hero__shine" aria-hidden="true" />
                    <div class="fso-performer-hero__gradient" aria-hidden="true" />
                    <button
                        class="fso-performer-hero__video-btn"
                        @click="openGoldVideo"
                        :aria-label="`Watch — What's GOLD?`"
                    >
                        <span class="fso-performer-hero__video-icon" aria-hidden="true" />
                        <span class="fso-performer-hero__video-text">What's GOLD?</span>
                    </button>
                    <div class="fso-performer-hero__nameplate">
                        <span v-if="performerData?.context" class="fso-performer-hero__eyebrow">{{ performerData.context }}</span>
                        <span class="fso-performer-hero__name">{{ performerData?.name }}</span>
                    </div>
                </div>

                <div
                    v-else-if="isTakeover"
                    class="fso-video-poster"
                    @click="openGoldVideo"
                >
                    <div class="fso-video-poster__bg" aria-hidden="true" />
                    <div class="fso-video-poster__shine" aria-hidden="true" />
                    <div class="fso-video-poster__bars" aria-hidden="true" />
                    <div class="fso-video-poster__play" aria-hidden="true">▶</div>
                    <div class="fso-video-poster__label">
                        <span>Watch — What's GOLD?</span>
                        <span class="fso-video-poster__label-len">0:42</span>
                    </div>
                </div>

                <!-- Make-it-Rain: gold stars (sibling so they spill into the body gap). -->
                <div v-if="isTakeover" class="fso-rain" aria-hidden="true">
                    <span
                        v-for="p in goldRain"
                        :key="p.id"
                        class="fso-rain__particle fso-rain__particle--star"
                        :style="{
                            left: p.left + '%',
                            width: p.size + 'px',
                            height: p.size + 'px',
                            animationDelay: p.delay + 's',
                            animationDuration: p.dur + 's',
                            '--rain-drift': p.drift + 'px',
                            '--rain-rot':   p.rot + 'deg',
                            '--rain-fall':  p.fall + 'px',
                        }"
                    />
                </div>

                <!-- Themed hero image (mothers-day) -->
                <div v-else-if="theme.image" class="fso-image-wrap">
                    <img :src="theme.image" alt="" class="fso-image" aria-hidden="true">
                    <div class="fso-image-overlay" />
                    <div class="fso-decorations" aria-hidden="true">
                        <span v-for="(deco, i) in theme.decorations" :key="i" class="fso-deco" :class="`fso-deco--${i + 1}`" v-html="deco" />
                    </div>
                </div>

                <!-- Emoji ring -->
                <div
                    v-else-if="decorationStyle === 'emoji-ring'"
                    class="fso-decorations fso-decorations--inline"
                    aria-hidden="true"
                >
                    <span v-for="(deco, i) in theme.decorations" :key="i" class="fso-deco" :class="`fso-deco--${i + 1}`" v-html="deco" />
                </div>

                <!-- Procedural star field -->
                <div v-else class="fso-decorations fso-decorations--inline" aria-hidden="true">
                    <div class="fso-stars-aura" aria-hidden="true" />
                    <span class="fso-star fso-star--hero" aria-hidden="true">
                        <img :src="STAR_IMG" alt="">
                    </span>
                    <span
                        v-for="s in starField"
                        :key="s.id"
                        class="fso-star"
                        :class="`fso-star--${s.anim}`"
                        :style="{
                            left: s.x + '%',
                            top: s.y + '%',
                            width: s.size + 'px',
                            height: s.size + 'px',
                            '--star-base-opacity': s.opacity,
                            filter: s.blur > 0 ? `blur(${s.blur}px)` : null,
                            animationDelay: s.delay + 's',
                            animationDuration: s.dur + 's',
                        }"
                    >
                        <img :src="STAR_IMG" alt="">
                    </span>
                    <div
                        v-for="(sh, i) in SHOOTING_STARS"
                        :key="`sh-${i}`"
                        class="fso-shooting"
                        :class="`fso-shooting--${i + 1}`"
                        :style="{ top: sh.top + '%' }"
                        aria-hidden="true"
                    >
                        <div
                            class="fso-shooting__streak"
                            :style="{ animationDelay: sh.delay + 's', animationDuration: sh.dur + 's' }"
                        />
                    </div>
                </div>

                <!-- Gold PSA video lightbox -->
                <Transition name="fso-video">
                    <div v-if="goldVideoUrl" class="fso-video-wrap" @click.self="closeGoldVideo">
                        <button class="fso-video-close" aria-label="Close video" @click="closeGoldVideo">&times;</button>
                        <video
                            :key="goldVideoUrl"
                            :src="goldVideoUrl"
                            class="fso-video"
                            autoplay
                            controls
                            playsinline
                        />
                    </div>
                </Transition>

                <div class="fso-body">
                    <div class="fso-badge fso-anim-in" style="animation-delay:0.15s">{{ theme.badge }}</div>

                    <!-- ── Title ───────────────────────────────────────
                         - Takeover + performer → "Loving {Name}?"
                         - Takeover, no performer → "You've got X stars."
                         - Control → theme title
                         ────────────────────────────────────────────── -->
                    <h2 v-if="isTakeover && hasPerformer" id="sgaOverlayTitle" class="fso-title fso-anim-in" style="animation-delay:0.25s">
                        Loving <span class="fso-title-accent">{{ performerData?.name }}</span>?
                    </h2>
                    <h2 v-else-if="isTakeover" id="sgaOverlayTitle" class="fso-title fso-anim-in" style="animation-delay:0.25s">
                        You've got <span class="fso-title-accent">{{ award.stars.toLocaleString() }} stars</span>.
                    </h2>
                    <h2 v-else id="sgaOverlayTitle" class="fso-title fso-anim-in" style="animation-delay:0.25s">
                        {{ theme.title }}
                    </h2>

                    <!-- Stars earned callout — only in performer-context takeover,
                         where the title is "Loving X?" and dynamicText is hidden. -->
                    <div v-if="isTakeover && hasPerformer && award.goal_stars > 0" class="fso-earned-note fso-anim-in" style="animation-delay:0.3s">
                        <img :src="STAR_IMG" alt="" class="fso-earned-note__star" aria-hidden="true">
                        <span>You just earned <strong>+{{ award.goal_stars.toLocaleString() }} star{{ award.goal_stars === 1 ? '' : 's' }}</strong>{{ award.goal_label ? ` for "${award.goal_label}"` : '' }}</span>
                    </div>

                    <div class="fso-amount fso-anim-in" style="animation-delay:0.35s">
                        <img :src="STAR_IMG" alt="Star" class="fso-amount-star">
                        <span class="fso-amount-number">{{ isTakeover ? userBalance.toLocaleString() : award.stars.toLocaleString() }}</span>
                        <span class="fso-amount-label">{{ isTakeover ? 'Stars in your balance' : 'Stars' }}</span>
                    </div>

                    <!-- Subhead is only shown in takeover WITHOUT performer
                         (when there's a performer, the title is already the question). -->
                    <h3 v-if="isTakeover && !hasPerformer" class="fso-subhead fso-subhead--amber fso-anim-in" style="animation-delay:0.4s">
                        Loving the drop?
                    </h3>

                    <p v-if="isTakeover && hasPerformer" class="fso-text fso-text--amber fso-text--lead fso-anim-in" style="animation-delay:0.42s">
                        Want more like this? With Gold, you get the full library — and a fresh star drop every month.
                    </p>
                    <p v-else-if="isTakeover" class="fso-text fso-text--amber fso-text--lead fso-anim-in" style="animation-delay:0.42s">
                        Your gift today is a one-time thing. With Gold, you get a fresh star drop every month — and the full library:
                    </p>
                    <p v-else class="fso-text fso-anim-in" style="animation-delay:0.42s">{{ dynamicText }}</p>

                    <!-- ── Perks list (takeover only) ──────────────── -->
                    <ul v-if="isTakeover" class="fso-gold-perks fso-anim-in" style="animation-delay:0.48s" aria-label="Gold member perks">
                        <li v-for="(perk, i) in GOLD_PERKS" :key="i" class="fso-gold-perks__item">
                            <span class="fso-gold-perks__check" aria-hidden="true" />
                            <span>{{ perk }}</span>
                        </li>
                    </ul>

                    <div class="fso-actions fso-actions--refined fso-anim-in" style="animation-delay:0.52s">
                        <template v-if="isTakeover">
                            <a
                                :href="goldUpgradeUrl"
                                class="fso-btn fso-btn--gold-primary"
                                @click="track('Campaign Overlay Upgrade to Gold Button Clicked')"
                            >
                                Upgrade to GOLD <span class="fso-btn-arrow" aria-hidden="true">→</span>
                            </a>
                            <a
                                :href="theme.cta_url"
                                class="fso-btn fso-btn--ghost-na"
                                @click="track('Campaign Overlay Browse Auditions Button Clicked')"
                            >
                                Use my stars now <span class="fso-btn-arrow" aria-hidden="true">→</span>
                            </a>
                        </template>
                        <template v-else>
                            <a :href="theme.cta_url" class="fso-btn fso-btn--primary-na" @click="track('Campaign Overlay Browse Auditions Button Clicked')">
                                {{ theme.cta }} <span class="fso-btn-arrow" aria-hidden="true">→</span>
                            </a>
                            <a :href="theme.cta2_url" class="fso-btn fso-btn--ghost-na" @click="track('Campaign Overlay Create Pitch Button Clicked')">
                                {{ theme.cta2 }} <span class="fso-btn-arrow" aria-hidden="true">→</span>
                            </a>
                        </template>

                        <!-- ── Upsell rows ────────────────────────── -->
                        <div v-if="isTakeover" class="fso-upsells">
                            <div class="fso-upsells__eyebrow">Just need more this once?</div>
                            <button
                                v-if="showStarsUpsell"
                                class="fso-upsell-row"
                                @click="handleBuyStarPack"
                            >
                                <span class="fso-upsell-row__icon fso-upsell-row__icon--star" aria-hidden="true">
                                    <img :src="STAR_IMG" alt="">
                                </span>
                                <span class="fso-upsell-row__copy">
                                    <span class="fso-upsell-row__title">{{ theme.cta_stars }}</span>
                                    <span class="fso-upsell-row__sub">Top up instantly</span>
                                </span>
                                <span class="fso-upsell-row__chev" aria-hidden="true">→</span>
                            </button>
                        </div>

                        <div v-else-if="showUpgradeUpsell || showStarsUpsell" class="fso-upsells">
                            <div class="fso-upsells__eyebrow">{{ upsellEyebrow }}</div>

                            <a
                                v-if="showUpgradeUpsell"
                                :href="upgradeUpsell.url"
                                class="fso-upsell-row"
                                :class="`fso-upsell-row--${upgradeUpsell.variant}`"
                                @click="track(upgradeUpsell.event)"
                            >
                                <span class="fso-upsell-row__icon" :class="`fso-upsell-row__icon--${upgradeUpsell.variant}`" aria-hidden="true">
                                    <svg v-if="upgradeUpsell.variant === 'gold'" class="fso-upsell-row__svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z" />
                                        <path d="M5 21h14" />
                                    </svg>
                                    <span v-else>★</span>
                                </span>
                                <span class="fso-upsell-row__copy">
                                    <span class="fso-upsell-row__title">
                                        {{ upgradeUpsell.title }}
                                        <span class="fso-upsell-row__chip">{{ upgradeUpsell.chip }}</span>
                                    </span>
                                    <span class="fso-upsell-row__sub">{{ upgradeUpsell.sub }}</span>
                                </span>
                                <span class="fso-upsell-row__chev" aria-hidden="true">→</span>
                            </a>

                            <button
                                v-if="upgradeUpsell && upgradeUpsell.variant === 'gold'"
                                class="fso-what-is-gold"
                                @click="openGoldVideo"
                            >
                                <span class="fso-what-is-gold__play" aria-hidden="true">▶</span>
                                Watch: What's GOLD?
                            </button>

                            <button
                                v-if="showStarsUpsell"
                                class="fso-upsell-row"
                                @click="handleBuyStarPack"
                            >
                                <span class="fso-upsell-row__icon fso-upsell-row__icon--star" aria-hidden="true">
                                    <img :src="STAR_IMG" alt="">
                                </span>
                                <span class="fso-upsell-row__copy">
                                    <span class="fso-upsell-row__title">{{ theme.cta_stars }}</span>
                                    <span class="fso-upsell-row__sub">Top up instantly</span>
                                </span>
                                <span class="fso-upsell-row__chev" aria-hidden="true">→</span>
                            </button>
                        </div>

                        <button class="fso-text-dismiss" @click="close('maybe-later')">Maybe later</button>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue'

const STAR_IMG = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

// Gold perks shown in the takeover layout. Tightly edited — 4 punchy
// lines is the sweet spot before perk fatigue kicks in.
const GOLD_PERKS = [
    'Monthly star drops — included with your plan',
    '12,500+ exclusive videos across 11 premium sites',
    '4K, HD & VR streaming · unlimited downloads',
    'New premium videos daily · free NA LIVE credits',
]

const props = defineProps({
    award: {
        type: Object,
        default: null, // { theme, stars, user_balance, goal_stars, campaign_bonus_stars, goal_label, campaign_completed, autocamp }
    },
    auth:    { type: Object },
    // 'control' | 'gold-takeover'  — A/B variant.
    variant: { type: String, default: 'control' },
    // Optional: { name, image, context } — when present and takeover is
    // active, the modal reframes around this performer.
    performer: { type: Object, default: null },
})

const emit = defineEmits(['close', 'buyStarPack'])

const THEMES = {
    default: {
        badge:        'Gift Claimed',
        title:        "You've got stars — use them.",
        text:         'Sponsor an audition to help a performer get filmed, or pitch your own scene idea and let the community decide.',
        cta:          'Browse Auditions',
        cta_url:      route('members2.reels.tag') + '?view=explore&sort=newest',
        cta2:         'Create a Pitch',
        cta2_url:     route('members2.studio.create-scene'),
        cta_stars:    'Buy Star Packs',
        image:        null,
        decorations:  [],
        css: {
            '--fso-card-bg':      'linear-gradient(145deg, #0f172a 0%, #1e293b 60%, #0f172a 100%)',
            '--fso-border':       'rgba(59,130,246,0.35)',
            '--fso-shadow-color': 'rgba(59,130,246,0.18)',
            '--fso-badge-bg':     'linear-gradient(135deg,#3b82f6,#2563eb)',
            '--fso-title-color':  '#f1f5f9',
            '--fso-amount-color': '#60a5fa',
            '--fso-text-color':   '#94a3b8',
            '--fso-btn-bg':       '#3b82f6',
            '--fso-btn-hover':    '#2563eb',
            '--fso-close-color':  '#64748b',
            '--fso-deco-size':    '28px',
            '--fso-glow':         'rgba(59,130,246,0.5)',
            '--fso-accent-1':          '#3b82f6',
            '--fso-accent-2':          '#2563eb',
            '--fso-accent-bright':     '#60a5fa',
            '--fso-accent-rgb':        '59, 130, 246',
            '--fso-accent-bright-rgb': '96, 165, 250',
        },
    },
    'mothers-day': {
        badge:        'Welcome Back 🌷',
        title:        "You've got stars — use them.",
        text:         "As a thank-you for coming back, we've loaded your account with complimentary stars. Sponsor an audition to help a performer get filmed, or pitch your own scene idea and let the community decide.",
        cta:          'Browse Auditions',
        cta_url:      route('members2.reels.tag') + '?view=explore&sort=newest',
        cta2:         'Create a Pitch',
        cta2_url:     route('members2.studio.create-scene'),
        cta_stars:    'Buy Star Packs',
        image:        'https://aws-lg.naughtycdn.com/mediaAssets/100514/ma_modal.png',
        decorations:  ['🌸', '🌹', '💐', '🌺', '🌷'], // overlay on hero image
        css: {
            '--fso-card-bg':      'linear-gradient(145deg, #1c0a14 0%, #2e1222 55%, #1c0a14 100%)',
            '--fso-border':       'rgba(236,72,153,0.4)',
            '--fso-shadow-color': 'rgba(236,72,153,0.2)',
            '--fso-badge-bg':     'linear-gradient(135deg,#e879a0,#be185d)',
            '--fso-title-color':  '#fce7f3',
            '--fso-amount-color': '#f9a8d4',
            '--fso-text-color':   '#e4aec5',
            '--fso-btn-bg':       '#be185d',
            '--fso-btn-hover':    '#9d174d',
            '--fso-close-color':  '#9d6b7f',
            '--fso-deco-size':    '30px',
            '--fso-glow':         'rgba(236,72,153,0.6)',
            '--fso-accent-1':          '#be185d',
            '--fso-accent-2':          '#831843',
            '--fso-accent-bright':     '#f9a8d4',
            '--fso-accent-rgb':        '190, 24, 93',
            '--fso-accent-bright-rgb': '249, 168, 212',
        },
    },
    'valentines-day': {
        badge:        'With Love 💌',
        title:        'Stars for the lover in you.',
        text:         "Hand-picked stars on us — spend them on the auditions that move you, or pitch a fantasy worth filming this season.",
        cta:          'Browse Auditions',
        cta_url:      route('members2.reels.tag') + '?view=explore&sort=newest',
        cta2:         'Create a Pitch',
        cta2_url:     route('members2.studio.create-scene'),
        cta_stars:    'Buy Star Packs',
        image:        null,
        decorations:  ['❤️', '💕', '💝', '🌹', '💌'], // → emoji-ring mode
        css: {
            '--fso-card-bg':      'linear-gradient(145deg, #1a0a0e 0%, #2e1217 55%, #1a0a0e 100%)',
            '--fso-border':       'rgba(225,29,72,0.4)',
            '--fso-shadow-color': 'rgba(225,29,72,0.22)',
            '--fso-badge-bg':     'linear-gradient(135deg, #fb7185, #e11d48)',
            '--fso-title-color':  '#ffe4e6',
            '--fso-amount-color': '#fb7185',
            '--fso-text-color':   '#e4aec5',
            '--fso-btn-bg':       '#e11d48',
            '--fso-btn-hover':    '#9f1239',
            '--fso-close-color':  '#9f6b76',
            '--fso-deco-size':    '30px',
            '--fso-glow':         'rgba(225,29,72,0.6)',
            '--fso-accent-1':          '#e11d48',
            '--fso-accent-2':          '#9f1239',
            '--fso-accent-bright':     '#fb7185',
            '--fso-accent-rgb':        '225, 29, 72',
            '--fso-accent-bright-rgb': '251, 113, 133',
        },
    },
    'memorial-day': {
        badge:        'In Honor 🇺🇸',
        title:        "Stars for the brave.",
        text:         "On this Memorial Day, we've loaded your account with stars as a thank-you. Sponsor an audition, pitch your own scene idea, or save them for what's next.",
        cta:          'Browse Auditions',
        cta_url:      route('members2.reels.tag') + '?view=explore&sort=newest',
        cta2:         'Create a Pitch',
        cta2_url:     route('members2.studio.create-scene'),
        cta_stars:    'Buy Star Packs',
        image:        null,
        decorations:  ['🇺🇸', '⭐', '🎖️', '🦅', '⭐'], // → emoji-ring mode
        css: {
            '--fso-card-bg':      'linear-gradient(145deg, #0a0f1c 0%, #0d1a2e 55%, #0a0f1c 100%)',
            '--fso-border':       'rgba(220,38,38,0.4)',
            '--fso-shadow-color': 'rgba(220,38,38,0.2)',
            '--fso-badge-bg':     'linear-gradient(135deg, #ef4444, #b91c1c)',
            '--fso-title-color':  '#f1f5f9',
            '--fso-amount-color': '#93c5fd',
            '--fso-text-color':   '#94a3b8',
            '--fso-btn-bg':       '#1d4ed8',
            '--fso-btn-hover':    '#1e40af',
            '--fso-close-color':  '#475569',
            '--fso-deco-size':    '30px',
            '--fso-glow':         'rgba(220,38,38,0.5)',
            '--fso-accent-1':          '#1d4ed8',
            '--fso-accent-2':          '#1e40af',
            '--fso-accent-bright':     '#93c5fd',
            '--fso-accent-rgb':        '29, 78, 216',
            '--fso-accent-bright-rgb': '147, 197, 253',
        },
    },
    anniversary: {
        badge:        'Anniversary Drop 🎉',
        title:        'A year together, on us.',
        text:         "We loaded your account with stars to mark the occasion. Back an audition, pitch your own scene, or save them for what's next.",
        cta:          'Browse Auditions',
        cta_url:      route('members2.reels.tag') + '?view=explore&sort=newest',
        cta2:         'Create a Pitch',
        cta2_url:     route('members2.studio.create-scene'),
        cta_stars:    'Buy Star Packs',
        image:        null,
        decorations:  [], // → star-field decoration mode (retinted violet)
        css: {
            '--fso-card-bg':      'linear-gradient(145deg, #0f0a1c 0%, #1c1138 55%, #0f0a1c 100%)',
            '--fso-border':       'rgba(139,92,246,0.4)',
            '--fso-shadow-color': 'rgba(139,92,246,0.22)',
            '--fso-badge-bg':     'linear-gradient(135deg, #a78bfa, #7c3aed)',
            '--fso-title-color':  '#f1ecff',
            '--fso-amount-color': '#c4b5fd',
            '--fso-text-color':   '#b8aed2',
            '--fso-btn-bg':       '#7c3aed',
            '--fso-btn-hover':    '#6d28d9',
            '--fso-close-color':  '#695b8a',
            '--fso-deco-size':    '28px',
            '--fso-glow':         'rgba(139,92,246,0.6)',
            '--fso-accent-1':          '#7c3aed',
            '--fso-accent-2':          '#4c1d95',
            '--fso-accent-bright':     '#c4b5fd',
            '--fso-accent-rgb':        '139, 92, 246',
            '--fso-accent-bright-rgb': '196, 181, 253',
        },
    },
}

const theme   = computed(() => THEMES[props.award?.theme] ?? THEMES.default)
const cssVars = computed(() => Object.entries(theme.value.css ?? {}).map(([k, v]) => `${k}:${v}`).join(';'))

const decorationStyle = computed(() => {
    if (theme.value.image) return 'hero-image'
    if (theme.value.decorations?.length > 0) return 'emoji-ring'
    return 'star-field'
})

// ── Membership-aware upsell logic (unchanged) ──
const isGold    = computed(() => props.auth?.is_gold)
const isExpired = computed(() => !props.auth?.has_reels_subscription)
const isTrial   = computed(() => props.auth?.is_trial)

const showStarsUpsell = computed(() => true)

const upgradeVariant = computed(() => {
    if (isTrial.value || isExpired.value) return 'account'
    if (!isGold.value) return 'gold'
    return null
})
const showUpgradeUpsell = computed(() => upgradeVariant.value !== null)

const upgradeUpsell = computed(() => {
    if (isExpired.value) {
        return { variant: 'account', title: 'Upgrade your account',
            sub: 'Pick up where you left off — reactivate now', chip: 'EXPIRED',
            url: route('members2.account.upgrade'),
            event: 'Campaign Overlay Expired Upgrade Button Clicked' }
    }
    if (isTrial.value) {
        return { variant: 'account', title: 'Upgrade your account',
            sub: 'Your trial ends soon — keep your perks', chip: 'TRIAL',
            url: route('members2.account.upgrade'),
            event: 'Campaign Overlay Trial Upgrade Button Clicked' }
    }
    return { variant: 'gold', title: 'Upgrade to', sub: 'Monthly star drops', chip: 'GOLD',
        url: route('members2.account.naGold', { source: 'sga_overlay' }),
        event: 'Campaign Overlay Upgrade to Gold Button Clicked' }
})

const upsellEyebrow = computed(() => {
    if (!showUpgradeUpsell.value) return 'Stock up on stars'
    if (isExpired.value)          return 'Welcome back — pick up where you left off'
    if (isTrial.value)            return 'Trial ending — keep your perks'
    return 'Want to go further?'
})

// ── Takeover activation ──
const isTakeover = computed(() =>
    props.variant === 'gold-takeover' && upgradeVariant.value === 'gold'
)

// ── Performer context ──
const performerData = computed(() => props.award?.performer ?? props.performer ?? null)
const hasPerformer  = computed(() =>
    isTakeover.value && !!performerData.value?.name && !!performerData.value?.image
)

const userBalance = computed(() => props.award?.user_balance ?? props.award?.stars ?? 0)

const goldUpgradeUrl = computed(() =>
    route('members2.account.naGold', { source: 'sga_overlay_takeover' })
)

const dynamicText = computed(() => {
    if (!props.award) return theme.value.text
    const { goal_stars, campaign_bonus_stars, goal_label, campaign_completed } = props.award
    if (goal_stars > 0 && goal_label) {
        let m = `You've got ${goal_stars.toLocaleString()} ${goal_stars === 1 ? 'star' : 'stars'} for ${goal_label}`
        if (campaign_completed && campaign_bonus_stars > 0) {
            m += ` and ${campaign_bonus_stars.toLocaleString()} extra ${campaign_bonus_stars === 1 ? 'star' : 'stars'} for completing the campaign`
        }
        return m + '!'
    }
    return theme.value.text
})

// ── Star field ──
const starField = computed(() => {
    const N = 22, out = []
    for (let i = 0; i < N; i++) {
        const s = i * 13.37 + 7
        out.push({
            id: i, x: (s * 17) % 100, y: (s * 29) % 100,
            size: 5 + ((s * 11) % 18),
            opacity: 0.25 + ((s * 13) % 75) / 100,
            blur: ((s * 19) % 22) / 10,
            delay: -((s * 23) % 40) / 10,
            dur: 2.6 + ((s * 31) % 38) / 10,
            anim: ['twinkle', 'drift', 'slowspin'][i % 3],
        })
    }
    return out
})
const SHOOTING_STARS = [
    { top: 22, delay: 1.2, dur: 2.4 },
    { top: 64, delay: 4.8, dur: 2.1 },
]

// ── Make-it-Rain ──
const goldRain = computed(() => {
    const N = 32, out = []
    for (let i = 0; i < N; i++) {
        const s = i * 7.13 + 3
        out.push({
            id: i, left: (s * 11) % 100,
            size: 5 + ((s * 5) % 8),
            delay: -((s * 13) % 70) / 10,
            dur: 2.4 + ((s * 7) % 32) / 10,
            drift: ((s * 17) % 60) - 30,
            rot: i % 2 === 0 ? 360 : -300,
            fall: 420,
        })
    }
    return out
})

watch(() => props.award, (val) => {
    if (val) track('Campaign Overlay Displayed')
})

function track(event) {
    if (typeof LTIAnalytics !== 'undefined') {
        LTIAnalytics.track(event, {
            autocamp:      props.award?.autocamp,
            is_trial:      isTrial.value,
            is_expired:    isExpired.value,
            is_gold:       isGold.value,
            variant:       isTakeover.value ? 'gold-takeover' : 'control',
            has_performer: hasPerformer.value,
        })
    }
}

function close(reason = 'close') {
    track(reason === 'maybe-later'
        ? 'Campaign Overlay Maybe Later Button Clicked'
        : 'Campaign Overlay Close Button Clicked')
    emit('close')
}

const handleBuyStarPack = () => {
    track('Campaign Overlay Buy Star Packs Button Clicked')
    emit('buyStarPack')
}

// ── Gold PSA video lightbox ──
const GOLD_VIDEOS = [
    'https://aws-lg.naughtycdn.com/mediaAssets/64933/Sara-Jay-Short.mp4',
    'https://aws-lg.naughtycdn.com/mediaAssets/64758/Taylor-Vixxen.mp4',
    'https://aws-lg.naughtycdn.com/mediaAssets/64757/Reagan-Foxx_1.mp4',
]
const goldVideoUrl = ref(null)

function openGoldVideo() {
    goldVideoUrl.value = GOLD_VIDEOS[Math.floor(Math.random() * GOLD_VIDEOS.length)]
    track('Campaign Overlay What Is Gold Button Clicked')
}
function closeGoldVideo() { goldVideoUrl.value = null }
</script>

<style scoped>
/* ── Sticky close button ── */
.fso-sticky-close {
    position: sticky; top: 0; z-index: 10;
    display: flex; align-items: center; justify-content: center;
    width: 100%; height: 36px;
    background: rgba(0,0,0,0.35);
    backdrop-filter: blur(6px);
    border: none; border-bottom: 1px solid rgba(255,255,255,0.06);
    color: rgba(255,255,255,0.5);
    font-size: 22px; line-height: 1;
    cursor: pointer;
    transition: color 0.2s, background 0.2s;
    font-family: inherit;
    flex-shrink: 0;
}
.fso-sticky-close:hover { color: rgba(255,255,255,0.9); background: rgba(0,0,0,0.55); }
.fso-card--gold-takeover .fso-sticky-close { border-bottom-color: rgba(251,191,36,0.12); }
.fso-card--gold-takeover .fso-sticky-close:hover { color: #FDE68A; }

/* Card retint */
.fso-card--gold-takeover {
    background: linear-gradient(145deg, #1a1208 0%, #2a1c0a 55%, #1a1208 100%) !important;
    border-color: rgba(251,191,36,0.4) !important;
    box-shadow: 0 32px 80px rgba(0,0,0,0.7), 0 0 70px rgba(251,191,36,0.16) !important;
}
.fso-card--gold-takeover .fso-orb {
    background: rgba(251,191,36,0.55) !important;
}
.fso-card--gold-takeover .fso-badge {
    background: linear-gradient(135deg, #FBBF24, #D97706) !important;
    color: #1c1917 !important;
}
.fso-card--gold-takeover .fso-body { padding-top: 22px; }
.fso-card--gold-takeover .fso-btn--ghost-na {
    background: rgba(253,230,138,0.05);
    color: rgba(253,230,138,0.9);
    border-color: rgba(251,191,36,0.3);
}
.fso-card--gold-takeover .fso-btn--ghost-na:hover {
    background: rgba(253,230,138,0.10);
    border-color: rgba(251,191,36,0.5);
    color: #FDE68A;
}
.fso-card--gold-takeover .fso-upsell-row__icon--star {
    background: rgba(251,191,36,0.16);
    border-color: rgba(251,191,36,0.3);
}

/* Title accent (the highlighted "X stars" span) */
.fso-title-accent { color: #FDE68A; }

/* Subhead — sits between the amount block and the body text in takeover */
.fso-earned-note {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(251, 191, 36, 0.12);
    border: 1px solid rgba(251, 191, 36, 0.3);
    border-radius: 999px;
    padding: 4px 12px 4px 6px;
    font-size: 12px;
    color: rgba(253, 230, 138, 0.9);
    margin-bottom: 10px;
}
.fso-earned-note strong { color: #fde68a; font-weight: 700; }
.fso-earned-note__star { width: 16px; height: 16px; flex-shrink: 0; }

.fso-subhead {
    font-size: 16px;
    font-weight: 700;
    margin: 4px 0 14px;
    line-height: 1.3;
    font-family: 'Montserrat', 'Inter', sans-serif;
    letter-spacing: 0.005em;
}
.fso-subhead--amber {
    color: #FDE68A;
    text-shadow: 0 0 18px rgba(251,191,36,0.4);
}

/* Amber body text */
.fso-text--amber { color: rgba(253,230,138,0.8) !important; }

/* Video poster (replaces star field / hero image / emoji ring) */
.fso-video-poster {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
}
/* Background lives in its own child so we can mask-fade it (along with
   the shine + bars + rain) into the card body — no hard horizontal seam. */
.fso-video-poster__bg {
    position: absolute; inset: 0; pointer-events: none;
    background:
        radial-gradient(60% 80% at 30% 40%, rgba(251,191,36,0.35), transparent 60%),
        radial-gradient(50% 70% at 80% 60%, rgba(217,119,6,0.4), transparent 60%),
        linear-gradient(135deg, #2a1c0a, #1a1208);
    -webkit-mask-image: linear-gradient(to bottom, black 0% 55%, transparent 100%);
    mask-image: linear-gradient(to bottom, black 0% 55%, transparent 100%);
}
.fso-video-poster__shine {
    position: absolute; inset: 0; pointer-events: none;
    background: radial-gradient(80% 100% at 50% 0%, rgba(253,230,138,0.18), transparent 70%);
    -webkit-mask-image: linear-gradient(to bottom, black 0% 55%, transparent 100%);
    mask-image: linear-gradient(to bottom, black 0% 55%, transparent 100%);
}
.fso-video-poster__bars {
    position: absolute; inset: 0; pointer-events: none;
    background: repeating-linear-gradient(0deg, transparent 0 3px, rgba(0,0,0,0.06) 3px 4px);
    mix-blend-mode: overlay;
    -webkit-mask-image: linear-gradient(to bottom, black 0% 55%, transparent 100%);
    mask-image: linear-gradient(to bottom, black 0% 55%, transparent 100%);
}
.fso-video-poster__play {
    position: relative;
    z-index: 5;
    width: 64px; height: 64px;
    border-radius: 50%;
    background: rgba(0,0,0,0.6);
    backdrop-filter: blur(4px);
    border: 2px solid rgba(253,230,138,0.65);
    display: inline-flex; align-items: center; justify-content: center;
    color: #FDE68A; font-size: 22px; padding-left: 5px;
    box-shadow: 0 0 0 8px rgba(251,191,36,0.12), 0 0 30px rgba(251,191,36,0.5);
    animation: fso-play-pulse 2.4s ease-in-out infinite;
}
@keyframes fso-play-pulse {
    0%, 100% { box-shadow: 0 0 0 0  rgba(251,191,36,0.4), 0 0 30px rgba(251,191,36,0.4); }
    50%      { box-shadow: 0 0 0 14px rgba(251,191,36,0),   0 0 40px rgba(251,191,36,0.6); }
}
.fso-video-poster__label {
    position: absolute;
    z-index: 5;
    bottom: 12px; left: 14px; right: 14px;
    display: flex; align-items: center; justify-content: space-between; gap: 8px;
    color: rgba(253,230,138,0.9);
    font-size: 11px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
}
.fso-video-poster__label-len {
    background: rgba(0,0,0,0.55); color: #fff;
    padding: 3px 8px; border-radius: 4px;
    letter-spacing: 0.1em; font-size: 10px;
}

/* Make-it-Rain layer
   Sibling of .fso-video-poster (direct child of .fso-card) so it isn't
   clipped to the 16:9 frame — particles spill into the gap above the
   body and dissolve via mask before reaching the title. */
.fso-rain {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 380px;
    overflow: hidden; pointer-events: none;
    z-index: 3;
    -webkit-mask-image: linear-gradient(to bottom,
    black 0% 45%,
    rgba(0,0,0,0.55) 75%,
    transparent 100%);
    mask-image: linear-gradient(to bottom,
    black 0% 45%,
    rgba(0,0,0,0.55) 75%,
    transparent 100%);
}
/* Bottom-fade overlay — DEPRECATED, no longer used. Kept in case it
   helps as a fallback layer for browsers without mask-image support. */
.fso-video-poster__fade {
    position: absolute; inset: 0;
    z-index: 1;
    pointer-events: none;
    background: linear-gradient(to bottom,
    transparent 0%,
    transparent 55%,
    rgba(26, 18, 8, 0.55) 80%,
    #1a1208 100%);
}
.fso-rain__particle {
    position: absolute; top: -24px;
    will-change: transform, opacity;
    animation-name: fso-rain-fall;
    animation-iteration-count: infinite;
    animation-timing-function: cubic-bezier(0.45, 0.05, 0.55, 1);
    animation-fill-mode: both;
}
.fso-rain__particle--coin {
    border-radius: 50%;
    background:
        radial-gradient(circle at 32% 28%, #FFF7DC 0%, #FDE68A 22%, #FBBF24 55%, #B45309 100%);
    box-shadow:
        0 0 6px rgba(251,191,36,0.55),
        inset 0 1px 0 rgba(255,255,255,0.55),
        inset 0 -2px 2px rgba(180,83,9,0.5);
}
.fso-rain__particle--star {
    background: linear-gradient(135deg, #FFF7DC, #FBBF24 55%, #D97706);
    clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
    filter: drop-shadow(0 0 4px rgba(251,191,36,0.85));
}
@keyframes fso-rain-fall {
    0%   { transform: translate(0, 0) rotate(0deg); opacity: 0; }
    12%  { opacity: 1; }
    88%  { opacity: 1; }
    100% {
        transform: translate(var(--rain-drift, 0px), var(--rain-fall, 320px))
        rotate(var(--rain-rot, 360deg));
        opacity: 0;
    }
}

@media (prefers-reduced-motion: reduce) {
    .fso-video-poster__play { animation: none; }
    .fso-rain__particle { animation: none; opacity: 0; }
}

/* Gold primary CTA */
.fso-btn--gold-primary {
    background: linear-gradient(135deg, #FBBF24, #D97706);
    color: #1c1917; border: none;
    width: 100%;
    padding: 15px 18px;
    font-size: 14.5px;
    border-radius: 12px;
    font-weight: 800;
    letter-spacing: 0.02em;
    box-shadow: 0 8px 24px rgba(251,191,36,0.35), inset 0 1px 0 rgba(255,255,255,0.4);
}
.fso-btn--gold-primary:hover {
    color: #1c1917;
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(251,191,36,0.55), inset 0 1px 0 rgba(255,255,255,0.5);
}
.fso-btn--gold-primary .fso-btn-arrow { color: #1c1917; }

/* ── Performer hero — replaces video poster when performer context ── */
.fso-performer-hero {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    background: linear-gradient(135deg, #2a1c0a, #1a1208);
}
.fso-performer-hero__bg {
    position: absolute; inset: 0;
    -webkit-mask-image: linear-gradient(to bottom, black 0% 55%, transparent 100%);
    mask-image: linear-gradient(to bottom, black 0% 55%, transparent 100%);
    animation: fso-performer-zoom 14s ease-in-out infinite alternate;
}
@keyframes fso-performer-zoom {
    from { transform: scale(1) translateY(0); }
    to   { transform: scale(1.06) translateY(-1%); }
}
.fso-performer-hero__shine {
    position: absolute; inset: 0; pointer-events: none;
    background:
        radial-gradient(60% 80% at 30% 30%, rgba(251,191,36,0.20), transparent 60%),
        radial-gradient(50% 70% at 80% 60%, rgba(217,119,6,0.18), transparent 60%);
    mix-blend-mode: screen;
    -webkit-mask-image: linear-gradient(to bottom, black 0% 55%, transparent 100%);
    mask-image: linear-gradient(to bottom, black 0% 55%, transparent 100%);
}
.fso-performer-hero__gradient {
    position: absolute; inset: 0; pointer-events: none;
    background: linear-gradient(to bottom,
    rgba(0,0,0,0) 0%,
    rgba(26,18,8,0) 50%,
    rgba(26,18,8,0.65) 78%,
    rgba(26,18,8,1) 100%);
}
.fso-performer-hero__nameplate {
    position: absolute; z-index: 5;
    bottom: 14px; left: 18px; right: 18px;
    text-align: left; color: #fff;
    display: flex; flex-direction: column; gap: 2px;
}
.fso-performer-hero__eyebrow {
    font-size: 10px; font-weight: 800;
    letter-spacing: 0.16em; text-transform: uppercase;
    color: rgba(253,230,138,0.85);
    text-shadow: 0 1px 2px rgba(0,0,0,0.6);
}
.fso-performer-hero__name {
    font-size: 22px; font-weight: 800;
    letter-spacing: -0.005em;
    color: #fff;
    font-family: 'Montserrat', 'Inter', sans-serif;
    text-shadow: 0 2px 12px rgba(0,0,0,0.55);
    line-height: 1.1;
}

/* ── "What's GOLD?" button — top-right of performer hero ─────────── */
.fso-performer-hero__video-btn {
    position: absolute; z-index: 5;
    top: 12px; right: 12px;
    display: inline-flex; align-items: center; gap: 8px;
    padding: 5px 14px 5px 5px;
    background: rgba(0,0,0,0.55);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(253,230,138,0.28);
    border-radius: 999px;
    color: #fff;
    font-family: inherit;
    font-size: 12px; font-weight: 600;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s, transform 0.15s, box-shadow 0.2s;
    box-shadow: 0 4px 14px rgba(0,0,0,0.35);
}
.fso-performer-hero__video-btn:hover {
    background: rgba(0,0,0,0.72);
    border-color: rgba(253,230,138,0.6);
    color: #FDE68A;
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(0,0,0,0.45), 0 0 0 4px rgba(251,191,36,0.08);
}
.fso-performer-hero__video-icon {
    width: 22px; height: 22px;
    border-radius: 50%;
    background: linear-gradient(135deg, #FDE68A, #D97706);
    display: inline-flex; align-items: center; justify-content: center;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.4), 0 2px 6px rgba(217,119,6,0.45);
    position: relative;
    flex: 0 0 auto;
}
.fso-performer-hero__video-icon::after {
    content: '';
    width: 0; height: 0;
    border-left: 7px solid #1c1917;
    border-top: 4px solid transparent;
    border-bottom: 4px solid transparent;
    margin-left: 2px;
}
.fso-performer-hero__video-text { line-height: 1; padding-bottom: 1px; }

/* ── Lead body text + Gold perks list ───────────────────────────── */
.fso-text--lead { margin-bottom: 14px !important; }

.fso-gold-perks {
    list-style: none;
    margin: 0 0 22px;
    padding: 14px 16px;
    text-align: left;
    background:
        linear-gradient(180deg, rgba(251,191,36,0.06) 0%, rgba(251,191,36,0.015) 100%),
        rgba(0,0,0,0.18);
    border: 1px solid rgba(251,191,36,0.22);
    border-radius: 12px;
    display: flex; flex-direction: column; gap: 9px;
}
.fso-gold-perks__item {
    display: flex; align-items: flex-start; gap: 10px;
    font-size: 12.5px; line-height: 1.4;
    color: rgba(255,255,255,0.92); font-weight: 500;
}
.fso-gold-perks__check {
    flex: 0 0 auto;
    width: 16px; height: 16px;
    margin-top: 1px;
    border-radius: 50%;
    background: linear-gradient(135deg, #FDE68A, #D97706 90%);
    position: relative;
    box-shadow: 0 0 6px rgba(251,191,36,0.45), inset 0 1px 0 rgba(255,255,255,0.45);
}
.fso-gold-perks__check::after {
    content: '';
    position: absolute;
    left: 4px; top: 5px;
    width: 8px; height: 4px;
    border-left: 2px solid #1c1917;
    border-bottom: 2px solid #1c1917;
    transform: rotate(-45deg);
}
</style>
